import { useCallback, useEffect, useMemo, useState } from 'react'
import { fetchCruiseSearchCount } from 'actions/CruiseActions'
import { shallowEqual, useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { usePlaceByName } from 'hooks/usePlace'
import useNavigateTo from 'hooks/useNavigateTo'
import getObjectKey from 'lib/object/getObjectKey'
import {
  buildSimilarCruiseSearchCandidates,
  selectSimilarCruiseSearchCandidate,
} from 'lib/cruises/cruiseSoldOutSimilarSearchUtils'

interface Props {
  arrivalDate?: string | null
  cabinType: string
  departureDate?: string | null
  offer: App.CruiseOffer
}

interface SimilarCruiseSearchUrl {
  isResolving: boolean
  navigateToSimilarCruises: () => void
}

function useSimilarCruiseSearchUrl({
  arrivalDate,
  cabinType,
  departureDate,
  offer,
}: Props): SimilarCruiseSearchUrl {
  const dispatch = useAppDispatch()
  const navigateTo = useNavigateTo()
  const [isResolving, setIsResolving] = useState(false)
  const departurePlace = usePlaceByName(offer.departurePort)
  const destinationPlace = usePlaceByName(offer.returnPort)

  const candidates = useMemo(() => buildSimilarCruiseSearchCandidates({
    arrivalDate,
    cabinType,
    cruiseLineCode: offer.cruiseLine.code,
    departureDate,
    departurePlace,
    destinationPlace,
    offer,
  }), [
    arrivalDate,
    cabinType,
    departureDate,
    departurePlace,
    destinationPlace,
    offer,
  ])

  const candidateKeys = useMemo(
    () => Object.values(candidates).map(candidate => getObjectKey(candidate.filters)),
    [candidates],
  )

  const countsByKey = useAppSelector(state => {
    const counts: Record<string, number | undefined> = {}
    candidateKeys.forEach(key => {
      const countState = state.cruise.cruiseSearchCount[key]
      counts[key] = countState?.fetching ? undefined : countState?.count
    })
    return counts
  }, shallowEqual)

  const selection = useMemo(() => selectSimilarCruiseSearchCandidate(
    candidates,
    filters => countsByKey[getObjectKey(filters)],
  ), [candidates, countsByKey])

  const navigateToSimilarCruises = useCallback(() => {
    setIsResolving(true)
  }, [])

  useEffect(() => {
    if (!isResolving) return

    if (selection.pendingCandidate) {
      dispatch(fetchCruiseSearchCount(selection.pendingCandidate.filters))
      return
    }

    navigateTo(selection.candidate.to)
    setIsResolving(false)
  }, [dispatch, isResolving, navigateTo, selection])

  return {
    isResolving,
    navigateToSimilarCruises,
  }
}

export default useSimilarCruiseSearchUrl
